// Procedural hex-terrain backdrop for the sign-in landing — a value-noise realm of
// water, meadow, forest and rock that the scrim then dissolves into shadow. Purely
// decorative (no game state); painted to a canvas sized to its container.

function hash2(x: number, y: number): number {
  // Integer hash with imul so low bits survive (plain * loses them in JS doubles).
  let h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + 1013904223) | 0
  h = Math.imul(h ^ (h >>> 13), 1274126177)
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}

function vnoise(x: number, y: number): number {
  const xi = Math.floor(x), yi = Math.floor(y)
  const xf = x - xi, yf = y - yi
  const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf)
  const a = hash2(xi, yi), b = hash2(xi + 1, yi), c = hash2(xi, yi + 1), d = hash2(xi + 1, yi + 1)
  return a * (1 - u) * (1 - v) + b * u * (1 - v) + c * (1 - u) * v + d * u * v
}

function fbm(x: number, y: number): number {
  let s = 0, amp = 0.5, freq = 1
  for (let i = 0; i < 4; i++) {
    s += amp * vnoise(x * freq, y * freq)
    freq *= 2
    amp *= 0.5
  }
  return s
}

/** Elevation → terrain colour, muted so the scrim can carry the mood. */
function terrainColor(e: number, m: number): string {
  if (e < 0.34) return '#243f57' // deep water
  if (e < 0.40) return '#33597a' // water
  if (e < 0.435) return '#bda067' // shore
  if (e < 0.57) return m > 0.52 ? '#587a42' : '#6b9350' // meadow
  if (e < 0.70) return '#3f5c33' // forest
  if (e < 0.80) return '#786f62' // rock
  return '#988f81' // high crag
}

/** Paint a pointy-top hex terrain field filling the canvas's client box. */
export function paintLandingMap(cv: HTMLCanvasElement): void {
  const rect = cv.getBoundingClientRect()
  const W = Math.max(1, Math.round(rect.width))
  const H = Math.max(1, Math.round(rect.height))
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  cv.width = Math.round(W * dpr)
  cv.height = Math.round(H * dpr)
  const ctx = cv.getContext('2d')
  if (!ctx) return
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, W, H)

  const S = 23 // hex circumradius (px)
  const SQRT3 = Math.sqrt(3)
  const hw = SQRT3 * S // column spacing
  const vh = 1.5 * S // row spacing
  const cols = Math.ceil(W / hw) + 2
  const rows = Math.ceil(H / vh) + 2
  const NS = 165 // noise scale (larger = broader biomes)

  ctx.lineJoin = 'round'
  for (let row = -1; row < rows; row++) {
    for (let col = -1; col < cols; col++) {
      const cx = col * hw + (row & 1 ? hw / 2 : 0)
      const cy = row * vh
      const e = fbm((cx + 400) / NS, (cy + 90) / NS)
      const m = fbm((cx - 700) / (NS * 1.4) + 40, (cy + 250) / (NS * 1.4) + 40)
      ctx.beginPath()
      for (let i = 0; i < 6; i++) {
        const ang = (Math.PI / 180) * (60 * i - 90)
        const x = cx + S * Math.cos(ang)
        const y = cy + S * Math.sin(ang)
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      }
      ctx.closePath()
      ctx.fillStyle = terrainColor(e, m)
      ctx.fill()
      // faint seam so the tiling reads as a hex grid
      ctx.strokeStyle = 'rgba(0,0,0,0.16)'
      ctx.lineWidth = 1
      ctx.stroke()
    }
  }
}
